import * as React from "react";
import { Routes, Route, Outlet, Link, useLocation, Navigate, useSearchParams } from "react-router-dom";
import "./App.css";
import MarketingLanding from "./MarketingLanding";
import HomePage from "./HomePage";
import Dashboard from "./dashboard";
import Login from "./login";
import ProtectedRoute from "./ProtectedRoute";
import SuperAdminRoute from "./SuperAdminRoute";
import SuperAdminApp from "./SuperAdminApp";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Button,
  Typography,
  Paper,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { glassTopNav, glassTopNavMenuPaper } from "./glass";
import { APP_TOP_NAV_HEIGHT_PX } from "./appChrome";
import { isAuthenticated } from "./auth";
import PublicThemeToggle from "./PublicThemeToggle";

const guestNavItems = [
  { label: "Home", to: "/" },
  { label: "Quote", to: "/quote" },
  { label: "Dashboard", to: "/dashboard" },
  { label: "Login", to: "/login" },
];

const authedNavItems = [
  { label: "Home", to: "/" },
  { label: "Quote", to: "/quote" },
  { label: "Dashboard", to: "/dashboard" },
  { label: "Account", to: "/dashboard/account" },
];

function isNavItemActive(pathname, to) {
  if (to === "/") return pathname === "/" || pathname.startsWith("/mow");
  if (to === "/quote") return pathname.startsWith("/quote");
  if (to === "/dashboard")
    return pathname.startsWith("/dashboard") && !pathname.startsWith("/dashboard/account");
  if (to === "/dashboard/account") return pathname.startsWith("/dashboard/account");
  if (to === "/login") return pathname.startsWith("/login");
  return false;
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/platform/*"
        element={
          <ProtectedRoute>
            <SuperAdminRoute>
              <SuperAdminApp />
            </SuperAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Layout />}>
        <Route index element={<MarketingLanding />} />
        <Route path="quote/:operatorSlug" element={<HomePage />} />
        <Route path="quote" element={<HomePage />} />
        <Route path="mow" element={<Navigate to="/quote" replace />} />
        <Route
          path="dashboard/*"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="login" element={<Login />} />
        <Route path="*" element={<NoMatch />} />
      </Route>
    </Routes>
  );
}

function Layout() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const embed = searchParams.get("embed") === "1";
  const authed = isAuthenticated();
  const navItems = authed ? authedNavItems : guestNavItems;
  const showAppChrome = !embed && location.pathname !== "/";
  const showQuoteAppearanceToggle = showAppChrome && location.pathname.startsWith("/quote");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const menuOpen = Boolean(menuAnchor);

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        pt: showAppChrome ? `${APP_TOP_NAV_HEIGHT_PX}px` : 0,
      }}
    >
      {showAppChrome ? (
        <Paper
          component="header"
          elevation={0}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            borderRadius: 0,
            ...glassTopNav,
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight: APP_TOP_NAV_HEIGHT_PX,
              px: { xs: 1.5, sm: 2 },
              maxWidth: 1200,
              mx: "auto",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box
              component={Link}
              to="/quote"
              aria-label="Clibbins — customer quotes"
              sx={{
                display: "flex",
                alignItems: "center",
                mr: 1.5,
                flexShrink: 0,
                textDecoration: "none",
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.55)",
                bgcolor: "rgba(255,255,255,0.35)",
                backdropFilter: "blur(18px) saturate(180%)",
                WebkitBackdropFilter: "blur(18px) saturate(180%)",
                boxShadow: `
                  inset 0 1px 0 rgba(255, 255, 255, 0.92),
                  0 2px 10px rgba(12, 48, 28, 0.06)
                `,
                transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.52)",
                  borderColor: "rgba(255,255,255,0.78)",
                  boxShadow: `
                    inset 0 1px 0 rgba(255, 255, 255, 0.96),
                    0 4px 14px rgba(12, 48, 28, 0.08)
                  `,
                },
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.15rem", sm: "1.3rem" },
                  letterSpacing: "-0.04em",
                  color: "primary.dark",
                  lineHeight: 1,
                  textShadow: "0 1px 0 rgba(255,255,255,0.92)",
                }}
              >
                Clibbins
              </Typography>
            </Box>
            {isMobile ? (
              <>
                <Box sx={{ flex: 1 }} />
                {showQuoteAppearanceToggle ? <PublicThemeToggle showLabel={false} /> : null}
                <IconButton
                  aria-label="Open menu"
                  aria-controls={menuOpen ? "app-nav-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuOpen ? "true" : undefined}
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{
                    color: "primary.dark",
                    borderRadius: 2,
                    border: "1px solid rgba(255,255,255,0.72)",
                    bgcolor: "rgba(255,255,255,0.48)",
                    backdropFilter: "blur(20px) saturate(180%)",
                    WebkitBackdropFilter: "blur(20px) saturate(180%)",
                    boxShadow: `
                      inset 0 1px 0 rgba(255, 255, 255, 0.9),
                      0 2px 10px rgba(12, 48, 28, 0.08)
                    `,
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.68)",
                      borderColor: "rgba(255,255,255,0.9)",
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="app-nav-menu"
                  anchorEl={menuAnchor}
                  open={menuOpen}
                  onClose={closeMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      minWidth: 216,
                      mt: 1,
                      borderRadius: "16px",
                      overflow: "hidden",
                      ...glassTopNavMenuPaper,
                    },
                  }}
                >
                  {navItems.map((item) => (
                    <MenuItem
                      key={item.to}
                      component={Link}
                      to={item.to}
                      onClick={closeMenu}
                      selected={isNavItemActive(location.pathname, item.to)}
                      sx={{
                        color: "text.primary",
                        typography: "body2",
                        fontWeight: 600,
                        py: 1.25,
                        "&.Mui-selected": {
                          color: "primary.dark",
                          bgcolor: "rgba(255,255,255,0.72)",
                          boxShadow: `
                            inset 0 1px 0 rgba(255, 255, 255, 0.95),
                            inset 3px 0 0 rgba(45, 106, 79, 0.55),
                            0 2px 8px rgba(0, 0, 0, 0.05)
                          `,
                          "&:hover": { bgcolor: "rgba(255,255,255,0.8)" },
                        },
                        "&:hover": { bgcolor: "rgba(255,255,255,0.45)" },
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <Stack
                direction="row"
                spacing={0.25}
                sx={{ flex: 1, justifyContent: "flex-end", alignItems: "center", flexWrap: "nowrap" }}
              >
                {showQuoteAppearanceToggle ? <PublicThemeToggle /> : null}
                {navItems.map((item) => {
                  const active = isNavItemActive(location.pathname, item.to);
                  return (
                    <Button
                      key={item.to}
                      component={Link}
                      to={item.to}
                      disableElevation
                      sx={{
                        color: active ? "primary.dark" : "text.secondary",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        textTransform: "none",
                        letterSpacing: "-0.01em",
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 2,
                        minWidth: "auto",
                        border: active ? "1px solid rgba(255,255,255,0.72)" : "1px solid transparent",
                        bgcolor: active ? "rgba(255,255,255,0.72)" : "transparent",
                        backdropFilter: active ? "blur(20px) saturate(180%)" : "none",
                        WebkitBackdropFilter: active ? "blur(20px) saturate(180%)" : "none",
                        boxShadow: active
                          ? `
                          inset 0 1px 0 rgba(255, 255, 255, 0.95),
                          0 2px 8px rgba(0, 0, 0, 0.06)
                        `
                          : "none",
                        "&:hover": {
                          color: "primary.dark",
                          bgcolor: "rgba(255,255,255,0.55)",
                          borderColor: "rgba(255,255,255,0.85)",
                          backdropFilter: "blur(16px) saturate(180%)",
                          WebkitBackdropFilter: "blur(16px) saturate(180%)",
                          boxShadow: `
                            inset 0 1px 0 rgba(255, 255, 255, 0.88),
                            0 2px 10px rgba(12, 48, 28, 0.06)
                          `,
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Stack>
            )}
          </Toolbar>
        </Paper>
      ) : null}
      <Outlet />
    </Box>
  );
}

function NoMatch() {
  return (
    <Box
      sx={{
        px: 3,
        py: 6,
        textAlign: "center",
        maxWidth: 400,
        mx: "auto",
      }}
    >
      <Typography variant="h5" gutterBottom>
        Page not found
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        That route doesn&apos;t exist.
      </Typography>
      <Button component={Link} to="/" variant="contained">
        Home
      </Button>
    </Box>
  );
}
